
import AppSendModal from "@/components/sales/SaleSendModal.vue";
import { ToolbarService } from "@/services/toolbar-service";
import Vue from "vue";
import { Sale } from "@/models/base";
import { SaleLayoutConf } from "../../models/sale-layout";
import { SaleService } from "../../services/sale-service";
import { UserConfigService } from "../../services/user-config-service";
import { UblService } from "../../plugins/ubl/services/ubl-service";
import AppTimeago from "@/components/common/Timeago.vue";
import AppPublicLink from "@/components/PublicLink.vue";
import AppSaleInfoOverview from "@/components/sales/SaleInfoOverview.vue";
import AppSaleHistory from "@/components/sales/SaleHistory.vue";
import AppSalePdfPreview from "@/components/sales/SalePdfPreview.vue";
import { PrintContext, SalePrintSettings } from "@/utils/sale-layouts/print-settings";
import { TranslateResult } from "vue-i18n";
import { ISelectItem } from "winfakt-vue-components";
import AppSalePrintExtraOptionsModal from "@/components/sales/SalePrintExtraOptionsModal.vue";

export default Vue.extend({
	data() {
		return {
			layout: new SaleLayoutConf(),
			sale: new Sale(),
			loaded: false,
			toSendFile: null as File | null,
			toSendFileName: "",
			sendModal: false,
			sendWithExtraPrintOptions: false,
			importReferencedSales: [] as Sale[],
			publicLinkModal: false,
			publicLink: "",
			salePrintModalOpen: false,
			activeTab: 0,
			printWithExtraOptionsModalOpen: false,
			isPrinting: false,
			isDownloading: false
		};
	},
	computed: {
		title(): TranslateResult {
			return ToolbarService.title;
		},
		isLgDown(){
			return this.$wf.isLgDown;
		},
		locale(): string {
			return this.$i18n.locale;
		},
		canEdit(): boolean {
			if (!UserConfigService.hasCurrentUserPermission((p) => p.SalePermissions.hasWritePermissions())) {
				return false;
			}

			if (!UserConfigService.hasCurrentUserPermission((p) => p.canManageSaleType(this.sale.TypeID))) {
				return false;
			}

			if (this.sale.IsSigned) {
				return false;
			}

			return true;
		},
		tabs(): any {
			let tabs = Object.values(this.$ct("InfoTabs"));
			if(this.isLgDown){
				tabs.push(Object.values(this.$ct("InfoTabsMobile")));
			}
			return tabs;
		},
		// eslint-disable-next-line max-lines-per-function
		toolbarActions(): ISelectItem<Function>[] {
			let result:ISelectItem<Function>[] = [
				{
					text: this.$ct("common.send"),
					value: this.openSendModal,
					icon: "envelope",
				}
			];
			if (this.$wf.isMdDown) {
				result.push(...this.extraSendActions);
			}
			result.push(
				{
					text: this.$ct("common.download"),
					value: this.download,
					icon: "download",
					disabled: this.isDownloading,
				},
				{
					text: this.$ct("common.print"),
					value: this.print,
					icon: "print",
					disabled: this.isPrinting,
				},
			);
			if (this.$wf.isMdDown) {
				result.push(...this.extraPrintActions);
			}
			result.push({
					text: this.$ct("public-url-create"),
					value: this.createPublicLink,
					icon: "link"
				},
				{
					text: this.$ct("download-ubl"),
					value: this.downloadUbl,
					icon: "file-invoice"
				}
			);
			return result;
		},
		extraPrintActions():ISelectItem<Function>[] {
			return [
				{
					text: this.$ct("print-with-extra-options"),
					value: this.openPrintExtraOptionsModal,
					icon: "cog"
				},
			];
		},
		extraSendActions():ISelectItem<Function>[]{
			return [
				{
					text: this.$ct("send-with-extra-options"),
					value: this.openSendExtraOptionsModal,
					icon: "cog"
				},
			];
		}
	},
	methods: {
		back() {
			this.$router.push(`/purchase-documents`);
		},
		edit() {
			this.$router.push(`/purchase-documents/edit/${this.sale.ID}`);
		},
		openPrintExtraOptionsModal(){
			this.printWithExtraOptionsModalOpen = true;
		},
		openSendExtraOptionsModal(){
			this.sendWithExtraPrintOptions = true;
			this.sendModal = true;
		},
		openSendModal() {
			this.sendWithExtraPrintOptions = false;
			this.sendModal = true;
		},
		async download() {
			try{
				this.isDownloading = true;
				await SaleService.downloadAndSaveSale(this.sale, this.layout);
				await this.refreshHistory();
			}finally{
				this.isDownloading = false;
			}
		},
		async printDuplicate(){
			await this.print(true);
		},
		async print(isDuplicate:boolean = false) {
			try{
				this.isPrinting = true;
				let settings = new SalePrintSettings(PrintContext.PRINT);
				settings.isDuplicate = isDuplicate;
				await SaleService.printSale(this.sale, settings.layoutId, settings);
				await this.refreshHistory();
			}finally{
				this.isPrinting = false;
			}
		},
		async refreshHistory() {
			this.sale.History = await SaleService.getSaleHistoryBySaleId(this.sale.ID);
		},
		mailSent() {
			this.sendModal = false;
			this.refreshHistory();
		},
		async downloadUbl() {
			await UblService.downloadInvoiceUbl(this, this.sale);
		},
		async fetchReferencedSales() {
			this.importReferencedSales = await SaleService.getReferencingImportSales(this.sale.ID);
		},
		openSale(saleId: number) {
			this.$router.push(`/purchase-documents/info/${saleId}`);
		},
		async createPublicLink() {
			this.publicLinkModal = true;
			if (!this.publicLink) {
				this.publicLink = await SaleService.generatePublicUrl(this.sale);
			}
		},
	},
	async mounted() {
		let id = parseInt(this.$route.params.id || "0");
		this.sale = await SaleService.getSale(id);
		this.layout = await SaleService.getSaleLayout(this.sale.getLayoutID(PrintContext.DOWNLOAD));
		this.loaded = true;
		this.fetchReferencedSales();

		let locale = this.$i18n.locale;
		let typeName = this.sale.Type.getDisplayedName(locale);
		ToolbarService.title = typeName + " " + this.sale.ComputedFriendlyID;
	},
	components: {
		AppSendModal,
		AppTimeago,
		AppPublicLink,
		AppSaleInfoOverview,
		AppSalePdfPreview,
		AppSalePrintExtraOptionsModal,
		AppSaleHistory
	},

	i18nEx: {
		componentPrefix: "components.sales"
	}
});
